import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Flex,
    Text,
    Spinner,
    Button,
    useDisclosure,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    Badge,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Card,
    CardBody,
    useToast,
    DrawerCloseButton,
    useBreakpointValue,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    DrawerFooter,
    Grid,
    GridItem,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Avatar,
    Image,
    Heading,
    Textarea,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    Input,
    HStack,
    Tooltip
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { MdStar, MdStarBorder, MdPhotoCamera } from 'react-icons/md';
import axios from 'axios';
import { toZonedTime, format } from 'date-fns-tz';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import SubscriptionCard from './SubscriptionCard';
import { driverPhotos, photoSrc } from '../../utils/driverPhotos';

const COURIERS = ['Uber', 'DoorDash'];
export const isCourier = (service) => COURIERS.includes(service);
export const courierNames = (order) =>
  [...new Set([order.pickupService, order.dropoffService].filter(isCourier))].join(' / ');
const COURIER_STATUS_LABELS = {
  pending: 'Finding a driver',
  pickup: 'Driver heading to pickup',
  pickup_complete: 'Picked up',
  dropoff: 'On the way',
  delivered: 'Delivered',
  canceled: 'Canceled',
  returned: 'Returned',
};
export const courierStatusLabel = (status) =>
  COURIER_STATUS_LABELS[(status || '').toLowerCase()] || (status || '').toUpperCase();

// "OrderSubmitted" -> "Order Submitted", "EnRouteToDelivery" -> "En Route To Delivery"
export const orderStatusLabel = (status) =>
  String(status || '').replace(/([a-z])([A-Z])/g, '$1 $2');


/**
 * Per-line bedding breakdown so a bedding service shows how its charge was
 * built (1st unit at full price, each additional at the bedding-additional
 * price) instead of a flat price × qty that wouldn't reconcile with the stored
 * subtotal. Mirrors the option-A ringing in the backend (pricing.py): the
 * single highest-priced unit across ALL bedding lines on the order is the one
 * "first at full"; every other unit rings at its own line's additional price.
 *
 * Returns a Map keyed by the service's index in `services`; each value is
 * { amount, label } (label e.g. "1st $35.00 + 1 × $25.00"). Non-bedding lines
 * are absent from the map (caller falls back to price × qty).
 */
function computeBeddingLineBreakdowns(services = []) {
    const units = [];
    (services || []).forEach((s, idx) => {
        if (!s || !s.isBedding) return;
        const n = Math.max(0, Math.round(parseFloat(s.weightOrCount || s.count || 0) || 0));
        const full = parseFloat(s.servicePrice || 0) || 0;
        const add = parseFloat(s.beddingAdditionalPrice != null ? s.beddingAdditionalPrice : 20) || 0;
        for (let k = 0; k < n; k++) units.push({ idx, full, add });
    });
    const result = new Map();
    if (units.length === 0) return result;
    let firstIdx = 0;
    units.forEach((u, i) => { if (u.full > units[firstIdx].full) firstIdx = i; });
    units.forEach((u, i) => {
        const isFirst = i === firstIdx;
        const priced = Math.round((isFirst ? u.full : u.add) * 100) / 100;
        const cur = result.get(u.idx) || { amount: 0, fullCount: 0, fullPrice: 0, addCount: 0, addPrice: 0 };
        cur.amount = Math.round((cur.amount + priced) * 100) / 100;
        if (isFirst) { cur.fullCount += 1; cur.fullPrice = u.full; }
        else { cur.addCount += 1; cur.addPrice = u.add; }
        result.set(u.idx, cur);
    });                                                                                                                                     
    result.forEach((v) => {
        const parts = [];
        if (v.fullCount > 0) parts.push(`1st $${v.fullPrice.toFixed(2)}`);
        if (v.addCount > 0) parts.push(`${v.addCount} × $${v.addPrice.toFixed(2)}`);
        v.label = parts.join(' + ');
    });
    return result;
}


const MyOrders = ({ customerId, laundryId, laundryTimeZone }) => {
    const [orders, setOrders] = useState([]);
    const [lastKey, setLastKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const [isCancelOrderLoading, setIsCancelOrderLoading] = useState(false);
    const initialLoadRef = useRef(true);
    const userAuthToken = localStorage.getItem('idToken');
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const {
        isOpen: isAlertOpen,
        onOpen: onAlertOpen,
        onClose: onAlertClose,
    } = useDisclosure();
    const cancelRef = useRef();
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const navigate = useNavigate();
    const {
        isOpen: isPaymentAlertOpen,
        onOpen: onPaymentAlertOpen,
        onClose: onPaymentAlertClose,
    } = useDisclosure();
    // Dialog for updating the saved card + retrying a failed auto-charge.
    const {
        isOpen: isUpdateCardOpen,
        onOpen: onUpdateCardOpen,
        onClose: onUpdateCardClose,
    } = useDisclosure();
    const [isProcessing, setIsProcessing] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const [shouldOpenDrawer, setShouldOpenDrawer] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get('order_id');
    const isOpenParam = queryParams.get('is_open');
    //Review Modal
    const {
        isOpen: isReviewModalOpen,
        onOpen: onReviewModalOpen,
        onClose: onReviewModalClose,
    } = useDisclosure();

    const handleOpenReviewModal = () => {
        onClose();
        setTimeout(() => {
            onReviewModalOpen();
        }, 200);
    };

    // Fetch Orders
    // State for cancellation reason and other explanation
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelRecurring, setIsCancelRecurring] = useState('');
    const [otherReason, setOtherReason] = useState('');
    
    // Available cancel types
    const cancelRecurringTypes = [
        'This order only',
        'This and future recurring orders'
    ];

    // Available cancellation reasons
    const cancellationOptions = [
        'Service no longer needed',
        'Order created by mistake',
        'Pickup/Delivery time no longer works',
        'Other'
    ];

    const fetchOrders = async (paginationKey) => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/get-orders-info`, {
                params: {
                    operation: 'getOrderDetails',
                    customerId: customerId,
                    laundryId: laundryId,
                    lastKey: paginationKey ? JSON.stringify(paginationKey) : null,
                },
                headers: {
                    'x-api-key': userAuthToken,
                },
            });
            const ordersData = response.data.body;
            console.log(ordersData);
            if (ordersData.status === 'success') {
                setOrders((prevOrders) => [...prevOrders, ...ordersData.data]);
                setLastKey(ordersData.lastKey ? JSON.parse(ordersData.lastKey) : null);
            } else {
                toast({
                    title: "Error Fetching Orders Info",
                    description: ordersData.message || 'Something went wrong',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast({
                title: 'Error Fetching Orders',
                description: error.response?.data?.message || 'Unable to fetch orders. Please try again later.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderDetails = async (orderId) => {
        setDetailsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/get-order-id-info`, {
                params: {
                    operation: 'getCustomerOrderInfo',
                    customerId: customerId,
                    orderId: orderId,
                },
                headers: {
                    'x-api-key': userAuthToken,
                },
            });

            // The endpoint's success payload is under response.data.body, but the
            // not-found / error path returns a bare {status, message} with no
            // `body`. Fall back to response.data so we never read `.status` /
            // `.message` off undefined (which crashed with "Cannot read
            // properties of undefined").
            const orderIdData = response.data?.body ?? response.data ?? {};
            console.log("orderId:",orderIdData);
            if (response.data?.statusCode === 200 && orderIdData?.status === 'success') {
                setOrderDetails(orderIdData.data);
            } else {
                toast({
                    title: "Order Not Found",
                    description: orderIdData?.message || 'No matching order for this ID.',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                navigate(`/${laundryId}/user/my-orders`);
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            toast({
                title: 'Error Fetching Order Details',
                description: error.response?.data?.message || 'Unable to fetch order details. Please try again later.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            navigate(`/${laundryId}/user/my-orders`);
        } finally {
            setDetailsLoading(false);
        }
    };

    // Cancel Order

    const handleCancelOrder = async () => {
        if (!isCancelRecurring) {
            toast({
                title: "Selection Required",
                description: "Please select the scope of your cancellation.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        if (!cancelReason) {
            toast({
                title: "Selection Required",
                description: "Please select a reason for cancellation.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (cancelReason === 'Other' && !otherReason.trim()) {
            toast({
                title: "Explanation Required",
                description: "Please provide an explanation for selecting 'Other'.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsCancelOrderLoading(true);
        try {
            const cancel_order_payload = {
                operation: "cancelOnlineOrder",
                customerId: customerId,
                orderId: selectedOrderId,
                laundryId: laundryId,
                cancelReason: cancelReason === 'Other' ? otherReason : cancelReason,
                isRecurring: isCancelRecurring == 'This order only' || isCancelRecurring == '' ? 'false' : 'true',
                address: orderDetails.address
            };

            const response = await axios.put(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/cancel-order`,
                cancel_order_payload,
                {
                    headers: {
                        'x-api-key': userAuthToken,
                    },
                }
            );

            const responseData = response.data;

            if (responseData.status === 'success') {
                toast({
                    title: "Success",
                    description: responseData.message,
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                setOrderDetails((prevDetails) => ({
                    ...prevDetails,
                    orderStatus: 'orderCanceled',
                }));
                setOrders((prevOrders) =>
                    prevOrders.map((order) =>
                        order.orderId === selectedOrderId ? { ...order, orderStatus: 'OrderCanceled' } : order
                    )
                );
                onClose();
            } else {
                toast({
                    title: "Error",
                    description: responseData.message || "Unable to cancel the order",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error canceling order:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || 'Unable to cancel the order. Please try again later.',
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsCancelOrderLoading(false);
            onAlertClose();
            setCancelReason('');
            setIsCancelRecurring('');
            setOtherReason('');
        }
    };

    const handleCancelClick = (orderId) => {
        setSelectedOrderId(orderId);
        setCancelReason('');
        setIsCancelRecurring('');
        setOtherReason('');
        onAlertOpen();
    };

    // Cancel Order Button functionality enable function
    const displayCancelOrder = () => {
        // Text orders can be booked about an hour before pickup. The 6-hour
        // website cutoff would hide Cancel for the whole life of those orders.
        // Status is already limited to OrderSubmitted by the button.
        if (orderDetails?.placedVia === 'text') return true;
        if (!orderDetails?.pickupDate) return false;
        const currentTime = toZonedTime(new Date(), laundryTimeZone);
        // Date-only orders have no time window — anchor the cutoff to the start
        // of the pickup date rather than crashing on a null interval.
        const startClock = orderDetails.pickupTimeInterval
            ? orderDetails.pickupTimeInterval.split(' - ')[0]
            : '00:00';
        const pickupStartTimeString = `${orderDetails.pickupDate}T${startClock}:00`;
        const pickupStartTime = toZonedTime(new Date(pickupStartTimeString), laundryTimeZone);

        const timeDifferenceInHours = (pickupStartTime - currentTime) / (1000 * 60 * 60);

        return timeDifferenceInHours >= 6;
    };

    const handleCardPayment = async () => {
        if (!stripe || !elements) {
            toast({
                title: "Error",
                description: "Stripe is not properly loaded.",
                status: "error",
                duration: 5000,
                isClosable: true
            });
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            toast({
                title: "Incomplete Card Details",
                description: "Please enter your card details to proceed.",
                status: "error",
                duration: 5000,
                isClosable: true
            });
            return;
        }

        if (!orderDetails) return;

        try {
            setIsProcessing(true);
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement
            });

            if (error) {
                toast({
                    title: "Card Payment Failed",
                    status: "error",
                    description: error.message || "Failed to finalize payment.",
                    duration: 3000,
                    isClosable: true
                });
                return;
            }

            // Call your backend to capture the payment
            const response = await axios.put(
                `${process.env.REACT_APP_AWS_API_URL}/api/payment/instore-online-payment`,
                {
                    cardPaymentMethodId: paymentMethod.id,
                    customerId: customerId
                },
                {
                    params: {
                        operation: "captureInStoreOrderOnlinePayment",
                        orderId: orderDetails.orderId,
                        laundryId: laundryId
                    },
                    headers: { "x-api-key": process.env.REACT_APP_AWS_API_KEY }
                }
            );

            if (response.data.body?.status === "success") {
                toast({
                    title: "Card Payment Successful",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
                setOrderDetails((prevDetails) => ({
                    ...prevDetails,
                    paymentStatus: "Paid"
                }));

                setOrders((prevOrders) =>
                    prevOrders.map((order) =>
                        order.orderId === orderDetails.orderId ? { ...order, paymentStatus: "Paid" } : order
                    )
                );

                onPaymentAlertClose();
                onClose();

                // Optionally refresh the orders or the single order details
            } else {
                toast({
                    title: "Card Payment Failed",
                    description: response.data.body?.message || "Failed to finalize payment.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            }
        } catch (err) {
            console.error(err);
            toast({
                title: "Payment Error",
                description: err.message || "Failed to process card payment.",
                status: "error",
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // Update the saved card, then retry the auto-charge for a failed order.
    // Flow: createPaymentMethod -> /save-card (attaches to the SAME Stripe
    // customer via customerPaymentId and sets it as the new default) ->
    // /retry-auto-charge (charges the new default card for the grand total).
    const handleUpdateCardAndCharge = async () => {
        if (!stripe || !elements) {
            toast({ title: "Payment form not ready", status: "error", duration: 4000, isClosable: true });
            return;
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            toast({ title: "Please enter your card details", status: "error", duration: 4000, isClosable: true });
            return;
        }
        if (!orderDetails) return;

        try {
            setIsProcessing(true);
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
            });
            if (error) {
                toast({
                    title: "Card Error",
                    description: error.message || "Invalid card details.",
                    status: "error", duration: 4000, isClosable: true,
                });
                return;
            }

            // 1) Save the new card and set it as the default payment method.
            const savePayload = {
                queryStringParameters: {
                    operation: 'saveCardDetails',
                    customerPaymentId: orderDetails.customerPaymentId || undefined,
                    customerId: customerId,
                    customerPaymentMethod: paymentMethod.id,
                    laundryId: laundryId,
                }
            };
            const saveResp = await axios.post(
                `${process.env.REACT_APP_AWS_API_URL}/api/payment/save-card`,
                savePayload,
                { headers: { 'x-api-key': userAuthToken } }
            );
            if (saveResp.data?.status !== 'success') {
                toast({
                    title: "Couldn't save card",
                    description: saveResp.data?.error || saveResp.data?.message || "Please try again.",
                    status: "error", duration: 5000, isClosable: true,
                });
                return;
            }

            // 2) Retry the charge against the new default card.
            const retryResp = await axios.post(
                `${process.env.REACT_APP_AWS_API_URL}/api/payment/retry-auto-charge`,
                { orderId: orderDetails.orderId, laundryId: laundryId },
                { headers: { 'x-api-key': userAuthToken } }
            );
            if (retryResp.data?.status === 'success') {
                toast({
                    title: "Payment successful",
                    description: "Your card was updated and the order has been charged.",
                    status: "success", duration: 4000, isClosable: true,
                });
                setOrderDetails((prev) => ({ ...prev, paymentStatus: 'Paid' }));
                setOrders((prev) => prev.map((o) =>
                    o.orderId === orderDetails.orderId ? { ...o, paymentStatus: 'Paid' } : o
                ));
                onUpdateCardClose();
            } else {
                // Card saved but charge still failed (e.g. new card also declined).
                toast({
                    title: "Card saved, but charge failed",
                    description: retryResp.data?.message || "Please try a different card.",
                    status: "warning", duration: 6000, isClosable: true,
                });
            }
        } catch (err) {
            console.error(err);
            toast({
                title: "Something went wrong",
                description: err.message || "Failed to update payment method.",
                status: "error", duration: 5000, isClosable: true,
            });
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            fetchOrders(null);
        }
    }, []);

    useEffect(() => {
        console.log("Route Query:", { orderId, isOpenParam });
        if (orderId && isOpenParam === 'true') {
            setShouldOpenDrawer(true);
            onOpen();
            fetchOrderDetails(orderId);
        } else {
            setShouldOpenDrawer(false);
        }
    }, [orderId, isOpenParam]);

    const handleOrderClick = (clickedOrderId) => {
        navigate(`/${laundryId}/user/my-orders?order_id=${clickedOrderId}&is_open=true`);
    };

    // Get color based on status
    const getOrderStatusColor = (status) => {
        switch (status) {
            case 'Delivered':
                return 'green.500';
            case 'ReadyForIntake':
            case 'ReceivedAtFacility':
            case 'ProcessingStarted':
            case 'ProcessingCompleted':
                return 'orange.500';
            case 'EnRouteToDelivery':
                return 'green.200';
            case 'OrderCanceled':
                return 'red.500';
            default:
                return 'blue.500';
        }
    };

    const getOrderTypeColor = (type) => {
        switch (type) {
            case 'InStore':
                return 'purple.500';
            case 'Online':
                return 'teal.500';
            default:
                return 'gray.500';
        }
    };
    const ReviewForm = ({ employee }) => {
        const fileInputRef = useRef();
        const [reviewData, setReviewData] = useState({
            rating: 0,
            comments: '',
            photo: null,
        });
        console.log(orderDetails)
        const [isProcessingImage, setIsProcessingImage] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Validate image file
        const validateImageFile = (file) => {
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic'];
            const maxSize = 5 * 1024 * 1024; // 5MB

            if (!validTypes.includes(file.type)) {
                toast({
                    title: 'Invalid File Type',
                    description: 'Please upload a JPG, JPEG, PNG, or HEIC image',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }

            if (file.size > maxSize) {
                toast({
                    title: 'File Too Large',
                    description: 'Maximum image size is 5MB',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }

            return true;
        };

        // Convert file to Base64
        const convertFileToBase64 = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });

        // Handle image upload and processing
        const handleImageUpload = async (file) => {
            if (!file) return null;

            // Validate the file first
            if (!validateImageFile(file)) {
                return null;
            }

            setIsProcessingImage(true);
            try {
                const imageBase64 = await convertFileToBase64(file);

                // Update state with the processed image
                setReviewData(prev => ({
                    ...prev,
                    photo: file,
                    photoBase64: imageBase64
                }));

                // Show success notification
                toast({
                    title: 'Image Attached',
                    description: 'Your image is ready',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });

                return imageBase64;
            } catch (error) {
                console.error('Error processing image:', error);
                toast({
                    title: 'Image Processing Failed',
                    description: 'Failed to process your image',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return null;
            } finally {
                setIsProcessingImage(false);
            }
        };

        const handleRatingChange = (newValue) => {
            setReviewData((prev) => ({...prev, rating: newValue}));
        };

        const handleCommentsChange = (e) => {
            setReviewData((prev) => ({...prev, comments: e.target.value}));
        };

        const handlePhotoChange = async (e) => {
            const file = e.target.files?.[0] || null;
            await handleImageUpload(file);
        };

        const handleSubmit = async () => {
            try {
                if (reviewData.rating === 0) {
                    toast({
                        title: 'Rating Required',
                        description: 'Please provide a rating before submitting',
                        status: 'warning',
                        duration: 3000,
                        isClosable: true,
                    });
                    return;
                }
                setIsSubmitting(true);

                let imageBase64 = null;
                if (reviewData.photo) {
                    imageBase64 = await handleImageUpload(reviewData.photo);
                    if (!imageBase64) return;
                }

                const payload = {
                    orderId: orderDetails.orderId,
                    customerId: customerId,
                    laundryId: laundryId,
                    employeeId: orderDetails.employee?.empId || orderDetails.tip?.tipReceiverId,
                    employeeRating: reviewData.rating,
                    orderDate: orderDetails.createdAt,
                    reviewComment: reviewData.comments,
                    ...(imageBase64 && { imageBase64 }),
                };

                const response = await axios.post(
                    `${process.env.REACT_APP_AWS_API_URL}/api/customer/create-review`,
                    payload,
                    {
                        headers: {
                            'x-api-key': userAuthToken,
                            'Content-Type': 'application/json',
                        },
                        params: {
                            operation: 'createReview',
                            laundryId: laundryId,
                        },
                    }
                );

                const responseBody =
                    typeof response.data.body === 'string'
                        ? JSON.parse(response.data.body)
                        : response.data.body;

                if (responseBody.status === 'success') {
                    toast({
                        title: 'Thanks for your feedback!',
                        description: responseBody.message || 'Your review has been submitted successfully',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });

                    setOrderDetails((prev) => ({ ...prev, isReviewed: true }));
                    setOrders((prevOrders) =>
                        prevOrders.map((order) =>
                            order.orderId === orderDetails.orderId ? { ...order, isReviewed: true } : order
                        )
                    );

                    onReviewModalClose();
                } else {
                    throw new Error(responseBody.message || 'Failed to submit review');
                }
            } catch (error) {
                console.error('Error submitting review:', error);
                toast({
                    title: 'Error Submitting Review',
                    description:
                        error.response?.data?.body?.message ||
                        error.response?.data?.message ||
                        error.message ||
                        'Failed to submit review',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setIsSubmitting(false);
            }
        };
        const StarRating = ({rating, onRatingChange}) => {
            const [hoverRating, setHoverRating] = useState(null);
            const starIndexes = [1, 2, 3, 4, 5];

            const display = hoverRating !== null ? hoverRating : rating;

            return (
                <Box display="flex">
                    {starIndexes.map((star) => (
                        <Box
                            as="span"
                            key={star}
                            cursor="pointer"
                            fontSize="2xl"
                            color={display >= star ? 'yellow.400' : 'gray.300'}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(null)}
                            onClick={() => onRatingChange(star)}
                            mx={0.5}
                        >
                            {display >= star ? <MdStar/> : <MdStarBorder/>}
                        </Box>
                    ))}
                </Box>
            );
        };
        const fullName = employee?.firstName && employee?.lastName &&
        employee.firstName !== "UU" && employee.lastName !== "UU"
            ? `${employee.firstName} ${employee.lastName}`
            : "your laundry specialist";

        const firstName = employee?.firstName &&
        employee.firstName.trim() &&
        employee.firstName !== "UU"
            ? employee.firstName
            : "them";

        return (
            <Box
                bg="gray.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={4}
            >
                <Box
                    bg="white"
                    w="100%"
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                >
                    <Avatar
                        size="xl"
                        name={fullName}
                        src={null}
                        mb={4}
                        mx="auto"
                    />

                    <Heading fontSize="2xl" mb={2}>
                        How did {fullName} do?
                    </Heading>
                    <Text mb={1} color="gray.600">
                        {fullName} washed and folded your clothes with care.
                    </Text>
                    <Text mb={4} color="gray.600">
                        Take a moment to let {firstName} know what you think.
                    </Text>

                    <Box mb={4} display="flex" justifyContent="center">
                        <StarRating
                            rating={reviewData.rating}
                            onRatingChange={handleRatingChange}
                        />
                    </Box>

                    <Textarea
                        value={reviewData.comments}
                        onChange={handleCommentsChange}
                        placeholder="Your comments…"
                        mb={4}
                        resize="vertical"
                        minH="100px"
                        maxLength={800}
                    />

                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        hidden
                        onChange={handlePhotoChange}
                    />

                    <Button
                        leftIcon={isProcessingImage ? <Spinner size="sm"/> : <MdPhotoCamera/>}
                        variant="outline"
                        onClick={() => fileInputRef.current.click()}
                        mb={4}
                        w="100%"
                        isLoading={isProcessingImage}
                        loadingText="Processing Image"
                        isDisabled={isProcessingImage}
                    >
                        {reviewData.photo ? 'Change Photo' : 'Add a Photo'}
                    </Button>

                    {reviewData.photo && (
                        <Box mb={4} position="relative">
                            <Image
                                src={URL.createObjectURL(reviewData.photo)}
                                alt="Photo Preview"
                                maxH="150px"
                                borderRadius="md"
                                mx="auto"
                            />
                            <Button
                                position="absolute"
                                top={1}
                                right={1}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => setReviewData(prev => ({
                                    ...prev,
                                    photo: null,
                                    photoBase64: null
                                }))}
                            >
                                ×
                            </Button>
                        </Box>
                    )}

                    <Button
                        colorScheme="blue"
                        onClick={handleSubmit}
                        w="100%"
                        isLoading={isSubmitting}
                        loadingText="Submitting..."
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        );
    };
    return (
        <Box p={[2, 4]} w="100%" display="flex" flexDirection="column" bg="#EBF8FF" overflow="hidden">
            <Box p={[2, 4]} borderBottomWidth="1px" zIndex="1" position="sticky" top="0">
                <Text fontSize={["xl", "2xl"]} fontWeight="bold" textAlign="center" color="blue.700">
                    My Orders
                </Text>
            </Box>

            <Box p={[2, 4]} overflowY="auto" mt={[2, 10]} flex="1" bg="#EBF8FF">
                {/* Subscription management card — shown when customer has active frequency */}
                <SubscriptionCard customerId={customerId} laundryId={laundryId} />

                {loading && orders.length === 0 ? (
                    <Flex justify="center" align="center" h="50vh">
                        <Spinner size="xl" />
                    </Flex>
                ) : orders.length === 0 ? (
                    <Flex justify="center" align="center" h="50vh">
                        <Text fontSize={["md", "lg"]} fontWeight="bold" color="gray.600">
                            No Orders Found
                        </Text>
                    </Flex>
                ) : (
                    <VStack spacing={4} align="stretch" p={[2, 4]}>
                        {orders.map((order) => (
                            <Box
                                key={order.orderId}
                                borderWidth="1px"
                                borderRadius="lg"
                                p={[2, 4]}
                                bg="#ccf0ed"
                                _hover={{ bg: 'gray.200' }}
                                cursor="pointer"
                                width="100%"
                                onClick={() => handleOrderClick(order.orderId)}
                            >
                                <Flex
                                    direction={{ base: 'column', md: 'row' }}
                                    justify="space-between"
                                    align={{ base: 'flex-start', md: 'center' }}
                                    mb={[2, 4]}
                                >
                                    <Box mb={{ base: 2, md: 0 }}>
                                        <Text fontSize={["md", "lg"]} fontWeight="bold">
                                            Order ID: {order.orderId}
                                        </Text>
                                        {order.locationLabel && (
                                            <Badge colorScheme="purple" variant="subtle" mt={1}>{order.locationLabel}</Badge>
                                        )}
                                    </Box>
                                    <Badge bgColor={getOrderTypeColor(order.orderType)} color="white" mt={{ base: 2, md: 0 }}>
                                        {order.orderType}
                                    </Badge>
                                </Flex>
                                <Flex
                                    direction={{ base: 'column', md: 'row' }}
                                    justify="space-between"
                                    align={{ base: 'flex-start', md: 'flex-start' }}
                                >
                                    <Box mb={{ base: 2, md: 0 }}>
                                        <Text fontSize={["sm", "md"]}>
                                            Date:{' '}
                                            {format(
                                                toZonedTime(new Date(order.createdAt), laundryTimeZone),
                                                'yyyy-MM-dd hh:mm a',
                                                { timeZone: laundryTimeZone }
                                            )}
                                        </Text>
                                        <Badge bgColor={getOrderStatusColor(order.orderStatus)} color="white" p={1} borderRadius="md" textTransform="none">
                                            {orderStatusLabel(order.orderStatus)}
                                        </Badge>
                                    </Box>
                                    <Box textAlign={{ base: 'left', md: 'right' }}>
                                        <Text fontSize={["sm", "md"]} color={order.paymentStatus === 'Failed' ? 'red.500' : undefined} fontWeight={order.paymentStatus === 'Failed' ? 'semibold' : undefined}>Payment Status: {(order.orderType === 'Commercial' || order.payByInvoice) && order.paymentStatus === 'Unpaid' ? 'Pay by Invoice' : order.paymentStatus === 'Failed' ? 'Payment Failed' : order.paymentStatus}</Text>
                                        <Text fontSize={["sm", "md"]} fontWeight="bold">
                                            Total Cost: ${Number(order.grandTotal ?? order.totalCost ?? 0).toFixed(2)}
                                        </Text>
                                    </Box>
                                </Flex>
                            </Box>
                        ))}
                        {lastKey && (
                            <Flex justify="center" mt={[2, 4]}>
                                <Button
                                    colorScheme="blue"
                                    onClick={() => fetchOrders(lastKey)}
                                    isLoading={loading}
                                    loadingText="Loading More"
                                >
                                    Load More
                                </Button>
                            </Flex>
                        )}
                    </VStack>
                )}
            </Box>

            <Drawer
                isOpen={isOpen && shouldOpenDrawer}
                onClose={() => {
                    navigate(`/${laundryId}/user/my-orders`);
                    onClose();
                }}
                size={isSmallScreen ? "md" : "sm"}
                placement={isSmallScreen ? "bottom" : "right"}
            >
                <DrawerOverlay />
                <DrawerContent maxH={isSmallScreen ? "80vh" : "100vh"} borderTopRadius={isSmallScreen ? "md" : "none"}>
                    <DrawerCloseButton variant="ghost" />
                    <DrawerHeader bg="blue.500" color="white" textAlign="center" fontSize={["xl", "2xl"]} fontWeight="bold">
                        Order Details
                    </DrawerHeader>
                    <DrawerBody bg="#ccf0ed" p={[2, 4]} overflowY="auto">
                        {detailsLoading ? (
                            <Flex justify="center" align="center" h="50vh">
                                <Spinner size="xl" />
                            </Flex>
                        ) : orderDetails ? (
                            <VStack spacing={[4, 6]} align="stretch">
                                <Box p={[2, 4]} borderWidth="1px" borderRadius=" basslg" bg="#ccf0ed" shadow="md">
                                    <Text fontSize={["lg", "xl"]} fontWeight="bold" color="blue.700" mb={[2, 4]}>
                                        Order Info
                                    </Text>
                                    <Grid templateColumns={["110px 1fr", "150px 1fr"]} rowGap={2}>
                                        <GridItem>
                                            <Text fontWeight="bold">Order ID:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.orderId}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Coupon:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.coupon || 'Not Available'}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Pickup Date{orderDetails.pickupTimeInterval ? '/Time' : ''}:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>
                                                {orderDetails.pickupDate}{orderDetails.pickupTimeInterval ? ` (${orderDetails.pickupTimeInterval})` : ''}
                                            </Text>
                                        </GridItem>

                                        <GridItem>
                                            <Text fontWeight="bold">Dropoff Date{orderDetails.dropoffTimeInterval ? '/Time' : ''}:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>
                                                {orderDetails.dropoffDate}{orderDetails.dropoffTimeInterval ? ` (${orderDetails.dropoffTimeInterval})` : ''}
                                                {orderDetails.dryCleanDropoffDate ? `. Dry cleaning back ${orderDetails.dryCleanDropoffDate}` : ''}
                                            </Text>
                                        </GridItem>

<GridItem colSpan={[2]} mt={4}>
  <HStack 
    align="center" 
    spacing={2} 
    px={2} 
    py={1} 
    bg="white" 
    borderRadius="md" 
    border="1px solid #CBD5E0"
  >
    {/* <Text fontWeight="bold" fontSize="md">Uber</Text> */}
    {(isCourier(orderDetails.pickupService) || isCourier(orderDetails.dropoffService)) && (
      <Accordion allowToggle flex="1" variant="unstyled">
        <AccordionItem border="none">
          <AccordionButton 
            _expanded={{ bg: "blue.50" }} 
            px={0} 
            py={1}
            _hover={{ bg: "blue.50" }}
          >
            <Box flex="1" textAlign="left" fontSize="md">
              🚗 {courierNames(orderDetails)} Details
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel 
            pb={4} 
            px={2} 
            bg="gray.50" 
            borderRadius="md" 
            mt={2}
          >
            {isCourier(orderDetails.pickupService) && (
              <Box mb={3}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={1}>Pickup ({orderDetails.pickupService})</Text>
                <VStack align="start" spacing={1} pl={2}>
                  {orderDetails.pickupTrackingUrl && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">📍 Tracking:</Text>
                      <a href={orderDetails.pickupTrackingUrl} target="_blank" rel="noopener noreferrer">
                        <Text fontSize="sm" color="blue.500" fontWeight="medium" _hover={{ textDecoration: "underline" }}>
                          View
                        </Text>
                      </a>
                    </HStack>
                  )}
                  {orderDetails.pickupStatus && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">📦 Status:</Text>
                      <Badge 
                        colorScheme={orderDetails.pickupStatus.toLowerCase() === "delivered" ? "green" : "purple"} 
                        fontSize="0.75em" 
                        px={2}
                      >
                        {courierStatusLabel(orderDetails.pickupStatus)}
                      </Badge>
                    </HStack>
                  )}
                  {orderDetails.pickupService === "Uber" && orderDetails.uberPickupFee && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">💵 Fee:</Text>
                      <Text fontSize="sm">${orderDetails.uberPickupFee.toFixed(2)}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}
            <Box borderTop="1px solid #CBD5E0" mb={3} />
            {isCourier(orderDetails.dropoffService) && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.700" mb={1}>Dropoff ({orderDetails.dropoffService})</Text>
                <VStack align="start" spacing={1} pl={2}>
                  {orderDetails.dropoffTrackingUrl && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">📍 Tracking:</Text>
                      <a href={orderDetails.dropoffTrackingUrl} target="_blank" rel="noopener noreferrer">
                        <Text fontSize="sm" color="blue.500" fontWeight="medium" _hover={{ textDecoration: "underline" }}>
                          View
                        </Text>
                      </a>
                    </HStack>
                  )}
                  {orderDetails.dropoffStatus && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">📦 Status:</Text>
                      <Badge 
                        colorScheme={orderDetails.dropoffStatus.toLowerCase() === "delivered" ? "green" : "purple"} 
                        fontSize="0.75em" 
                        px={2}
                      >
                        {courierStatusLabel(orderDetails.dropoffStatus)}
                      </Badge>
                    </HStack>
                  )}
                  {orderDetails.dropoffService === "Uber" && orderDetails.uberDropoffFee && (
                    <HStack spacing={2}>
                      <Text fontSize="sm">💵 Fee:</Text>
                      <Text fontSize="sm">${orderDetails.uberDropoffFee.toFixed(2)}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    )}
  </HStack>
</GridItem>
                                     

                                        <GridItem>
                                            <Text fontWeight="bold">Special Instructions:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.specialInstructions || 'No Special Instructions'}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Laundry Bags:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.laundryBags || 'N/A'}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Status:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderStatusLabel(orderDetails.orderStatus)}</Text>
                                        </GridItem>
                                        

                                        {orderDetails.pickupService === 'Uber' && orderDetails.uber?.uberFee && (
                                        <>
                                            <GridItem>
                                            <Text fontWeight="bold">Uber Fee:</Text>
                                            </GridItem>
                                            <GridItem>
                                            <Text>${(orderDetails.uber.uberFee / 100).toFixed(2)}</Text>
                                            </GridItem>
                                        </>
                                        )}
                                    </Grid>
                                    {/* Multi-address ("Airbnb cleaner"): per-property breakdown.
                                        Each property is weighed + billed separately; the invoice
                                        itemizes by property. */}
                                    {orderDetails.isMultiAddress && Array.isArray(orderDetails.segments) && orderDetails.segments.length > 0 && (
                                        <Box mt={[2, 4]} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                            <Text fontWeight="bold" mb={2}>🏠 By Property</Text>
                                            <VStack align="stretch" spacing={1}>
                                                {orderDetails.segments.map((seg, sIdx) => (
                                                    <HStack key={seg.id || sIdx} justify="space-between" fontSize="sm">
                                                        <Text noOfLines={1}>
                                                            {seg.label || `Property ${sIdx + 1}`}
                                                            {seg.totalWeight != null && (
                                                                <Text as="span" color="gray.500"> · {Number(seg.totalWeight).toFixed(1)} lb</Text>
                                                            )}
                                                        </Text>
                                                        <Text fontWeight="600">${Number(seg.subTotal || 0).toFixed(2)}</Text>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                            <Text fontSize="xs" color="gray.500" mt={2}>
                                                Each property is weighed separately. Your invoice is itemized by property.
                                            </Text>
                                        </Box>
                                    )}

                                    <Text fontWeight="bold" mt={[2, 4]} mb={[1, 2]}>Services:</Text>
                                    <Table variant="simple" size="sm" overflowX="auto">
                                        <Thead>
                                            <Tr>
                                                <Th>Service</Th>
                                                <Th isNumeric>Qty</Th>
                                                <Th isNumeric>Unit Price</Th>
                                                <Th isNumeric>Line Total</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {(() => {
                                                // For multi-property ("Airbnb") orders, group the service
                                                // rows by property and show a property header before each
                                                // group so the customer can see which weight/charge belongs
                                                // to which address. Single-address orders render flat.
                                                const services = orderDetails.services || [];
                                                const isMulti = orderDetails.isMultiAddress &&
                                                    Array.isArray(orderDetails.segments) &&
                                                    orderDetails.segments.length > 0;

                                                // Bedding lines show "1st full / each additional" pricing so the
                                                // per-line total reconciles with the stored subtotal (a flat
                                                // price × qty would overstate a bedding line).
                                                const beddingLines = computeBeddingLineBreakdowns(services);

                                                const renderServiceRow = (service, key, svcIndex) => {
                                                    const actualQty = Number(service.weightOrCount);
                                                    const billedQty = service.billedWeight != null
                                                        ? Number(service.billedWeight)
                                                        : actualQty;
                                                    const wasFloored = billedQty > actualQty;
                                                    const bl = beddingLines.get(svcIndex);
                                                    const lineTotal = bl ? bl.amount : Number(service.servicePrice) * billedQty;
                                                    return (
                                                        <Tr key={key}>
                                                            <Td>
                                                                {service.serviceName}
                                                                {wasFloored && (
                                                                    <Text fontSize="xs" color="orange.600">
                                                                        Billed at {billedQty} lb minimum (actual {actualQty} lb)
                                                                    </Text>
                                                                )}
                                                                {bl && bl.label && (
                                                                    <Text fontSize="xs" color="gray.500">
                                                                        {bl.label}
                                                                    </Text>
                                                                )}
                                                            </Td>
                                                            <Td isNumeric>{wasFloored ? billedQty : actualQty}</Td>
                                                            <Td isNumeric>${Number(service.servicePrice).toFixed(2)}</Td>
                                                            <Td isNumeric>${lineTotal.toFixed(2)}</Td>
                                                        </Tr>
                                                    );
                                                };

                                                if (!isMulti) {
                                                    return services.map((service, index) => renderServiceRow(service, index, index));
                                                }

                                                const rows = [];
                                                const used = new Set();
                                                orderDetails.segments.forEach((seg, sIdx) => {
                                                    const group = services
                                                        .map((s, i) => ({ s, i }))
                                                        .filter(({ s }) => s.segmentId === seg.id);
                                                    if (group.length === 0) return;
                                                    rows.push(
                                                        <Tr key={`hdr-${seg.id || sIdx}`} bg="blue.50">
                                                            <Td colSpan={4}>
                                                                <Text fontSize="xs" fontWeight="bold" color="blue.700" noOfLines={1}>
                                                                    🏠 {seg.label || `Property ${sIdx + 1}`}
                                                                    {seg.totalWeight != null && (
                                                                        <Text as="span" color="gray.500" fontWeight="normal"> · {Number(seg.totalWeight).toFixed(1)} lb</Text>
                                                                    )}
                                                                </Text>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                    group.forEach(({ s, i }) => { used.add(i); rows.push(renderServiceRow(s, i, i)); });
                                                });
                                                // Any unsegmented services (legacy/edge) render without a header.
                                                services.forEach((s, i) => { if (!used.has(i)) rows.push(renderServiceRow(s, i, i)); });
                                                return rows;
                                            })()}
                                            {(orderDetails.addons || []).map((addon, aIdx) => (
                                                <Tr key={`addon-${aIdx}`}>
                                                    <Td>
                                                        {addon.addonName}
                                                        <Text fontSize="xs" color="gray.500">
                                                            Add-on{addon.pricingBasis === 'per_pound' ? ' (per lb)' : ''}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric>{Number(addon.quantity)}{addon.pricingBasis === 'per_pound' ? ' lb' : ''}</Td>
                                                    <Td isNumeric>${Number(addon.unitPrice).toFixed(2)}</Td>
                                                    <Td isNumeric>${Number(addon.amount).toFixed(2)}</Td>
                                                </Tr>
                                            ))}
                                            {(orderDetails.products || []).map((prod, pIdx) => {
                                                const count = Number(prod.productCount != null ? prod.productCount : (prod.quantity || 0)) || 0;
                                                const price = Number(prod.productPrice != null ? prod.productPrice : (prod.price || 0)) || 0;
                                                return (
                                                    <Tr key={`prod-${pIdx}`}>
                                                        <Td>
                                                            {prod.productName}
                                                            <Text fontSize="xs" color="gray.500">Product</Text>
                                                        </Td>
                                                        <Td isNumeric>{count}</Td>
                                                        <Td isNumeric>${price.toFixed(2)}</Td>
                                                        <Td isNumeric>${(price * count).toFixed(2)}</Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                    {/* Pricing breakdown */}
                                    <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                                        {orderDetails.subTotal > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Subtotal</Text>
                                                <Text>${Number(orderDetails.subTotal).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        {orderDetails.discountedPrice > 0 && (() => {
                                            // Derive the discount source label + effective %.
                                            const sub = Number(orderDetails.subTotal) || 0;
                                            const disc = Number(orderDetails.discountedPrice) || 0;
                                            const pct = sub > 0 ? Math.round((disc / sub) * 100) : 0;
                                            const hasCoupon = orderDetails.coupon && orderDetails.coupon !== 'None';
                                            let source;
                                            if (hasCoupon) {
                                                source = `Coupon ${orderDetails.coupon}`;
                                            } else if (orderDetails.frequency) {
                                                source = orderDetails.pricingType === 'per_bag'
                                                    ? 'Subscribe & Save'
                                                    : 'Recurring plan';
                                            } else {
                                                source = 'Discount';
                                            }
                                            const label = pct > 0 ? `${source} (${pct}% off)` : source;
                                            return (
                                                <Flex justify="space-between" fontSize="sm" color="green.600" mb={1}>
                                                    <Text>{label}</Text>
                                                    <Text>−${disc.toFixed(2)}</Text>
                                                </Flex>
                                            );
                                        })()}
                                        {orderDetails.tip?.tipAmount > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Tip</Text>
                                                <Text>${Number(orderDetails.tip.tipAmount).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        {Number(orderDetails.serviceTax) > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Service Tax{Number(orderDetails.serviceTaxRate) > 0 ? ` (${orderDetails.serviceTaxRate}%)` : ''}</Text>
                                                <Text>${Number(orderDetails.serviceTax).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        {Number(orderDetails.productTax) > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Product Tax{Number(orderDetails.productTaxRate) > 0 ? ` (${orderDetails.productTaxRate}%)` : ''}</Text>
                                                <Text>${Number(orderDetails.productTax).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        {/* Fallback: combined line if no split provided but tax exists. */}
                                        {!(Number(orderDetails.serviceTax) > 0 || Number(orderDetails.productTax) > 0) && Number(orderDetails.taxAmount) > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Sales Tax</Text>
                                                <Text>${Number(orderDetails.taxAmount).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        {orderDetails.deliveryFee > 0 && (
                                            <Flex justify="space-between" fontSize="sm" color="gray.600" mb={1}>
                                                <Text>Delivery fee{orderDetails.deliveryDistanceMi != null ? ` (${Number(orderDetails.deliveryDistanceMi).toFixed(1)} mi)` : ''}</Text>
                                                <Text>${Number(orderDetails.deliveryFee).toFixed(2)}</Text>
                                            </Flex>
                                        )}
                                        <Flex justify="space-between" fontWeight="bold" fontSize="md" mt={1}>
                                            <Text>Total</Text>
                                            <Text>${Number(orderDetails.grandTotal || orderDetails.totalCost).toFixed(2)}</Text>
                                        </Flex>
                                    </Box>
                                </Box>
                                <Box p={[2, 4]} borderWidth="1px" borderRadius="lg" bg="#ccf0ed" shadow="md">
                                    <Text fontSize={["lg", "xl"]} fontWeight="bold" color="blue.700" mb={[2, 4]}>
                                        Address
                                    </Text>
                                    <Grid templateColumns={["110px 1fr", "150px 1fr"]} rowGap={2}>
                                        <GridItem>
                                            <Text fontWeight="bold">Address:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.address || 'Not Available'}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Instructions:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.addressInstructions || 'Not Available'}</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text fontWeight="bold">Door Number:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text>{orderDetails.doorNumber || 'Not Available'}</Text>
                                        </GridItem>
                                    </Grid>
                                </Box>
                                <Box p={[2, 4]} borderWidth="1px" borderRadius="lg" bg="#ccf0ed" shadow="md">
                                    <Text fontSize={["lg", "xl"]} fontWeight="bold" color="blue.700" mb={4}>
                                        Payment
                                    </Text>
                                    <Grid templateColumns={["110px 1fr", "150px 1fr"]} rowGap={2}>
                                        <GridItem>
                                            <Text fontWeight="bold">Status:</Text>
                                        </GridItem>
                                        <GridItem>
                                            <Text color={orderDetails.paymentStatus === 'Failed' ? 'red.500' : undefined} fontWeight={orderDetails.paymentStatus === 'Failed' ? 'semibold' : undefined}>
                                                {(orderDetails.orderType === 'Commercial' || orderDetails.payByInvoice) && orderDetails.paymentStatus === 'Unpaid'
                                                    ? 'Pay by Invoice'
                                                    : orderDetails.paymentStatus === 'Failed'
                                                        ? 'Payment Failed'
                                                        : orderDetails.paymentStatus}
                                            </Text>
                                            {orderDetails.paymentStatus === 'Failed' && (
                                                <Text fontSize="sm" color="red.500" mt={1}>
                                                    We couldn't charge your card on file. Update your payment method to complete this order.
                                                </Text>
                                            )}
                                        </GridItem>
                                        {orderDetails.tip ? (
                                            <>
                                                <GridItem>
                                                    <Text fontWeight="bold">Tip Type:</Text>
                                                </GridItem>
                                                <GridItem>
                                                    <Text>{orderDetails.tip.tipType}</Text>
                                                </GridItem>
                                                {orderDetails.tip.tipType === 'percentage' && (
                                                    <>
                                                        <GridItem>
                                                            <Text fontWeight="bold">Tip %:</Text>
                                                        </GridItem>
                                                        <GridItem>
                                                            <Text>{orderDetails.tip.tipPercentage}%</Text>
                                                        </GridItem>
                                                    </>
                                                )}
                                                {orderDetails.tip.tipType === 'amount' && (
                                                    <>
                                                        <GridItem>
                                                            <Text fontWeight="bold">Tip Amount:</Text>
                                                        </GridItem>
                                                        <GridItem>
                                                            <Text>${orderDetails.tip.tipAmount}</Text>
                                                        </GridItem>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <GridItem>
                                                    <Text fontWeight="bold">Tip:</Text>
                                                </GridItem>
                                                <GridItem>
                                                    <Text color="gray.600">No Tip Applied</Text>
                                                </GridItem>
                                            </>
                                        )}
                                    </Grid>
                                    {orderDetails.accountFunds && (
                                        <Box mt={[2, 3]} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="green.300">
                                            <Flex justify="space-between" mb={1}>
                                                <Text>Order total</Text>
                                                <Text>${Number(orderDetails.grandTotal || 0).toFixed(2)}</Text>
                                            </Flex>
                                            <Flex justify="space-between" mb={1} color="green.700">
                                                <Text>Paid from your account balance</Text>
                                                <Text>−${Number(orderDetails.accountFunds.usedOnOrder || 0).toFixed(2)}</Text>
                                            </Flex>
                                            <Flex justify="space-between" fontWeight="bold" borderTopWidth="1px" pt={1} mb={1}>
                                                <Text>{orderDetails.payByInvoice && Number(orderDetails.balanceDue) > 0 ? 'Amount due (on invoice)' : 'Amount due'}</Text>
                                                <Text>${Number(orderDetails.balanceDue || 0).toFixed(2)}</Text>
                                            </Flex>
                                            <Flex justify="space-between" fontSize="sm" color="gray.600">
                                                <Text>Remaining account balance</Text>
                                                <Text fontWeight="semibold">${Number(orderDetails.accountFunds.remainingBalance || 0).toFixed(2)}</Text>
                                            </Flex>
                                        </Box>
                                    )}
                                    {orderDetails.finalPaymentIntentId && orderDetails.finalPaymentIntentId.length > 0 ? (
                                        orderDetails.finalPaymentIntentId.map((payment, index) => (
                                            <Card key={index} mt={[2, 3]} borderWidth="0.1px" shadow="sm">
                                                <CardBody>
                                                    <Flex>
                                                        <Text fontWeight="bold" mr={2}>Amount:</Text>
                                                        <Text>${payment.amount}</Text>
                                                    </Flex>
                                                    <Flex>
                                                        <Text fontWeight="bold" mr={2}>Payment Method:</Text>
                                                        <Text>{payment.paymentMethod === 'AccountFunds' ? 'Account balance (prepaid)' : payment.paymentMethod}</Text>
                                                    </Flex>
                                                </CardBody>
                                            </Card>
                                        ))
                                    ) : (
                                        <Text mt={[2, 4]} color="gray.600" fontStyle="italic">
                                            No Payment Received Yet
                                        </Text>
                                    )}
                                </Box>

                                {/* Laundry Photos (pickup/scale/fold/tracking) */}
                                {(orderDetails.imageUrl || orderDetails.weightImageUrl || orderDetails.foldImageUrl || orderDetails.intakeImageUrls?.length || orderDetails.foldTrackingImageUrls?.length) && (
                                    <Box mt={4} p={3} bg="white" borderRadius="md" boxShadow="sm">
                                        <Text fontWeight="600" fontSize="sm" mb={2}>📷 Laundry Photos</Text>
                                        <HStack spacing={3} flexWrap="wrap" align="flex-start">
                                            {driverPhotos(orderDetails).map((photo) => (
                                                <Box key={photo.key}>
                                                    <Text fontSize="xs" color="gray.600" fontWeight="600">{photo.label}</Text>
                                                    <Text fontSize="xs" color="gray.500" mb={1}>{photo.takenAt || '\u00A0'}</Text>
                                                    <Image src={photoSrc(photo.url)} alt={photo.label} maxH="150px" borderRadius="md" objectFit="cover" />
                                                </Box>
                                            ))}
                                            {orderDetails.weightImageUrl && orderDetails.weightImageUrl.split('|||').map((url, idx) => {
                                                const trimmedUrl = url.trim();
                                                const imgSrc = trimmedUrl.startsWith('data:') || trimmedUrl.startsWith('http') ? trimmedUrl : `data:image/jpeg;base64,${trimmedUrl}`;
                                                return (
                                                <Box key={`weight-${idx}`}>
                                                    <Text fontSize="xs" color="gray.500" mb={1}>Bag {idx + 1}</Text>
                                                    <Image src={imgSrc} alt={`Weight photo ${idx + 1}`} maxH="150px" borderRadius="md" objectFit="cover" />
                                                </Box>
                                                );
                                            })}
                                            {orderDetails.foldImageUrl && (
                                                <Box>
                                                    <Text fontSize="xs" color="gray.500" mb={1}>Fold</Text>
                                                    <Image src={orderDetails.foldImageUrl.startsWith('data:') || orderDetails.foldImageUrl.startsWith('http') ? orderDetails.foldImageUrl : `data:image/jpeg;base64,${orderDetails.foldImageUrl}`} alt="Fold photo" maxH="150px" borderRadius="md" objectFit="cover" />
                                                </Box>
                                            )}
                                            {orderDetails.intakeImageUrls && orderDetails.intakeImageUrls.map((url, idx) => (
                                                <Box key={`intake-${idx}`}>
                                                    <Text fontSize="xs" color="gray.500" mb={1}>Count {idx + 1}</Text>
                                                    <Image src={url} alt={`Count photo ${idx + 1}`} maxH="150px" borderRadius="md" objectFit="cover" />
                                                </Box>
                                            ))}
                                            {orderDetails.foldTrackingImageUrls && orderDetails.foldTrackingImageUrls.map((url, idx) => (
                                                <Box key={`fold-track-${idx}`}>
                                                    <Text fontSize="xs" color="gray.500" mb={1}>Folded {idx + 1}</Text>
                                                    <Image src={url} alt={`Fold photo ${idx + 1}`} maxH="150px" borderRadius="md" objectFit="cover" />
                                                </Box>
                                            ))}
                                        </HStack>
                                    </Box>
                                )}

                                {/* View garment photos / AI count — opens the order tracking
                                    photos page. Shown when the tenant has Advanced AI Garment
                                    Tracking (with customer-photo consent) OR the order simply
                                    has photos on file. Tenants without advanced tracking and
                                    orders with no photos never see this link. */}
                                {(orderDetails.advancedTrackingCustomerPhotos
                                    || (orderDetails.hasPhotos && orderDetails.countShareMode && orderDetails.countShareMode !== 'off')) && (
                                    <Button
                                        variant="outline"
                                        colorScheme="blue"
                                        size="md"
                                        mt={[2, 3]}
                                        onClick={() => window.open(
                                            `/order-tracking/${orderDetails.orderId}?laundryId=${laundryId}`,
                                            '_blank'
                                        )}
                                    >
                                        📷 {orderDetails.countShareMode === 'counts' && !orderDetails.advancedTrackingCustomerPhotos
                                            ? 'View Item Counts' : 'View Garment Photos'}
                                    </Button>
                                )}

                                {/* Track Driver Button - show when order is in trackable state */}
                                {((orderDetails.orderStatus === 'OrderSubmitted' && orderDetails.pickupService === 'LaundryDriver') ||
                                  (orderDetails.orderStatus === 'EnRouteToDelivery' && orderDetails.dropoffService === 'LaundryDriver')) && (
                                    <Button
                                        colorScheme="teal"
                                        size="lg"
                                        mt={[2, 4]}
                                        onClick={() => navigate(`/${laundryId}/user/track/${orderDetails.orderId}`)}
                                    >
                                        Track Driver
                                    </Button>
                                )}

                                {orderDetails.orderStatus === 'OrderSubmitted' && displayCancelOrder(orderDetails, laundryTimeZone) && (
                                    <Button
                                        colorScheme="red"
                                        size="lg"
                                        mt={[2, 4]}
                                        onClick={() => handleCancelClick(orderDetails.orderId)}
                                        isDisabled={detailsLoading || orderDetails.orderStatus === 'canceled'}
                                        isLoading={isCancelOrderLoading}
                                    >
                                        Cancel Order
                                    </Button>
                                )}
                                {/* Review Button - show for any non-canceled order that hasn't been reviewed */}
                                {!orderDetails.isReviewed && orderDetails.orderStatus !== 'OrderCanceled' && orderDetails.orderStatus !== 'OrderSubmitted' && (
                                    <Button
                                        colorScheme="blue"
                                        size="lg"
                                        mt={[2, 4]}
                                        onClick={handleOpenReviewModal}
                                        isDisabled={detailsLoading}
                                    >
                                        Review Order
                                    </Button>
                                )}
                            </VStack>
                        ) : (
                            <Text>No Details Found</Text>
                        )}
                    </DrawerBody>
                    {orderDetails && (
                        <DrawerFooter bg="#ccf0ed" borderTopWidth="1px" display="flex" justifyContent="center">
                            {orderDetails && (() => {
                                const { orderType, orderStatus, paymentStatus, payByInvoice } = orderDetails;
                                // Commercial/pay-by-invoice orders never show Pay Now (they use Stripe Invoicing)
                                if (orderType === 'Commercial' || payByInvoice) {
                                    return null;
                                }
                                // Auto-charge failed — let the customer update their card and retry.
                                if (paymentStatus === 'Failed' && orderStatus !== 'OrderCanceled') {
                                    return (
                                        <Button
                                            colorScheme="red"
                                            width="100%"
                                            onClick={onUpdateCardOpen}
                                        >
                                            Update Payment Method
                                        </Button>
                                    );
                                }
                                // Already paid or canceled — no button needed
                                if (paymentStatus === 'Paid' || orderStatus === 'OrderCanceled') {
                                    return null;
                                }
                                // Show Pay Now for any unpaid order that's past initial submission
                                const payableStatuses = ['ReceivedAtFacility', 'ProcessingStarted', 'ProcessingCompleted', 'EnRouteToDelivery', 'Delivered', 'OrderPickedUp'];
                                const shouldEnablePayNow = paymentStatus === 'Unpaid' && payableStatuses.includes(orderStatus);
                                if (!shouldEnablePayNow) {
                                    return null;
                                }
                                return (
                                    <Button
                                        colorScheme="blue"
                                        width="100%"
                                        onClick={onPaymentAlertOpen}
                                    >
                                        Pay Now
                                    </Button>
                                );
                            })()}
                        </DrawerFooter>
                    )}
                </DrawerContent>
            </Drawer>
            <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={onAlertClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Cancel Order
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text className="mb-4">Are you sure you want to cancel this order? This action cannot be undone.</Text>
                            <FormControl isRequired>
                                <FormLabel>Cancel Scope:</FormLabel>
                                <RadioGroup onChange={setIsCancelRecurring} value={isCancelRecurring}>
                                    <VStack align="start" spacing={2}>
                                        {cancelRecurringTypes.map((type) => (
                                            <Radio key={type} value={type}>
                                                {type}
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Please select the reason for cancellation:</FormLabel>
                                <RadioGroup onChange={setCancelReason} value={cancelReason}>
                                    <VStack align="start" spacing={2}>
                                        {cancellationOptions.map((reason) => (
                                            <Radio key={reason} value={reason}>
                                                {reason}
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                                {cancelReason === 'Other' && (
                                    <FormControl isRequired mt={4}>
                                        <FormLabel>Please explain your reason:</FormLabel>
                                        <Input
                                            value={otherReason}
                                            onChange={(e) => setOtherReason(e.target.value)}
                                            placeholder="Enter your reason"
                                        />
                                    </FormControl>
                                )}
                            </FormControl>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onAlertClose}>
                                No
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleCancelOrder}
                                ml={3}
                                isLoading={isCancelOrderLoading}
                            >
                                Yes, Cancel Order
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
            <AlertDialog
                isOpen={isPaymentAlertOpen}
                onClose={onPaymentAlertClose}
                leastDestructiveRef={cancelRef}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Pay for Order {orderDetails?.orderId}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {orderDetails && (() => {
                                const baseCost = orderDetails.totalCost || 0;
                                let tipAmount = 0;
                                if (orderDetails.tip?.tipType === 'percentage') {
                                    tipAmount = (orderDetails.tip.tipPercentage / 100) * baseCost;
                                } else {
                                    tipAmount = orderDetails.tip?.tipAmount || 0;
                                }
                                const taxAmount = Number(orderDetails.taxAmount) || 0;
                                // Charge the authoritative grand total when available
                                // (it already includes tax); otherwise reconstruct it
                                // as cost + tip + tax so tax is never dropped.
                                const totalPayable = Number(orderDetails.grandTotal)
                                    || (baseCost + tipAmount + taxAmount);
                                return (
                                    <>
                                        <Text mb={[1, 2]}>Total Cost: ${baseCost.toFixed(2)}</Text>
                                        <Text mb={[1, 2]}>Tip Amount: ${tipAmount.toFixed(2)}</Text>
                                        {taxAmount > 0 && (
                                            <Text mb={[1, 2]}>Sales Tax: ${taxAmount.toFixed(2)}</Text>
                                        )}
                                        <Text mb={[2, 4]} fontWeight="semibold">
                                            Amount to Pay: ${totalPayable.toFixed(2)}
                                        </Text>
                                    </>
                                );
                            })()}
                            <Box width="100%">
                                <Text mb={[1, 2]}>Enter Card Details</Text>
                                <Box border="1px solid #ccc" p={[2, 4]} borderRadius="fm" mb={2}>
                                    <CardElement
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: "16px",
                                                    color: "#424770",
                                                    "::placeholder": { color: "#aab7c4" },
                                                },
                                                invalid: { color: "#9e2146" },
                                            },
                                        }}
                                    />
                                </Box>
                                <Button
                                    colorScheme="blue"
                                    width="100%"
                                    onClick={handleCardPayment}
                                    isLoading={isProcessing}
                                >
                                    Submit Card Payment
                                </Button>
                            </Box>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onPaymentAlertClose}>
                                Cancel
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Update Payment Method + retry charge (failed auto-charge) */}
            <AlertDialog
                isOpen={isUpdateCardOpen}
                onClose={onUpdateCardClose}
                leastDestructiveRef={cancelRef}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Update Payment Method
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text mb={[2, 3]}>
                                We couldn't charge your card on file for order {orderDetails?.orderId}.
                                Enter a new card below to complete this order.
                            </Text>
                            {orderDetails && (
                                <Text mb={[2, 4]} fontWeight="semibold">
                                    Amount to Pay: ${Number(orderDetails.grandTotal ?? orderDetails.totalCost ?? 0).toFixed(2)}
                                </Text>
                            )}
                            <Box width="100%">
                                <Text mb={[1, 2]}>Enter Card Details</Text>
                                <Box border="1px solid #ccc" p={[2, 4]} borderRadius="md" mb={2}>
                                    <CardElement
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: "16px",
                                                    color: "#424770",
                                                    "::placeholder": { color: "#aab7c4" },
                                                },
                                                invalid: { color: "#9e2146" },
                                            },
                                        }}
                                    />
                                </Box>
                                <Button
                                    colorScheme="blue"
                                    width="100%"
                                    onClick={handleUpdateCardAndCharge}
                                    isLoading={isProcessing}
                                    loadingText="Processing"
                                >
                                    Save Card & Pay
                                </Button>
                            </Box>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onUpdateCardClose} isDisabled={isProcessing}>
                                Cancel
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Review Modal */}
            <Modal
                isOpen={isReviewModalOpen}
                onClose={onReviewModalClose}
                size="xl"
                isCentered
                scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent
                    maxH="calc(100vh - 40px)"
                    display="flex"
                    flexDirection="column"
                >
                    <ModalHeader
                        position="sticky"
                        top={0}
                        bg="white"
                        zIndex="sticky"
                        borderBottomWidth="1px"
                        pr={10}
                    >
                        Leave a Review
                        <ModalCloseButton
                            size="lg"
                            position="absolute"
                            right={4}
                            top={4}
                            _hover={{ bg: "gray.100" }}
                        />
                    </ModalHeader>
                    <ModalBody
                        p={6}
                        overflowY="auto"
                        flex="1"
                    >
                        <ReviewForm employee={orderDetails?.employee} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default MyOrders;